// Presentational parts of the Reports area (props in, markup out; rendered by the pages and the Today
// widgets, and in unit tests with react-dom/server).
import { formatDateTime } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Badge, Button, DataTable, EmptyState, type Column } from "@ady/ui";
import type { BreakdownRow, Leaderboard, Report, ReportExport, SeriesPoint } from "./api";
import { HEADLINES, formatFigure, keyLabel, metricLabel } from "./format";

export const ATID = {
  page: "analytics-reports",
  figures: "analytics-figures",
  table: "analytics-table",
  planLocked: "analytics-plan-locked",
  trafficActivation: "analytics-traffic-activation",
  partial: "analytics-partial-profit",
  export: "analytics-export",
  exportStatus: "analytics-export-status",
  leaderboard: "analytics-leaderboard",
  leaderboardToggle: "analytics-leaderboard-toggle",
  widgets: "analytics-widgets",
  widget: "analytics-widget",
  consent: "analytics-consent",
} as const;

function Stat({ metric, value, hint }: { metric: string; value: number; hint?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5 rounded-sm bg-sunken px-3 py-2.5"
      data-metric={metric}
    >
      <span className="text-xs text-fg-secondary">{metricLabel(metric)}</span>
      <span className="ady-ledger text-title-sm font-semibold">{formatFigure(metric, value)}</span>
      {hint ? <span className="text-2xs text-fg-tertiary">{hint}</span> : null}
    </div>
  );
}

/** The headline figures of a report (with the previous period on advanced plans). */
export function ReportFigures({ report, limit }: { report: Report; limit?: number }) {
  const keys = (HEADLINES[report.topic] ?? Object.keys(report.totals)).filter(
    (k) => k in report.totals,
  );
  return (
    <div
      className={
        // Three or four figures (Today widgets) fill the card in equal columns.
        limit === 3
          ? "grid grid-cols-3 gap-2"
          : limit === 4
            ? "grid grid-cols-2 gap-2 sm:grid-cols-4"
            : "grid grid-cols-2 gap-2 lg:grid-cols-5"
      }
      data-testid={ATID.figures}
    >
      {keys.slice(0, limit ?? keys.length).map((k) => (
        <Stat
          key={k}
          metric={k}
          value={report.totals[k] ?? 0}
          {...(report.previous && k in report.previous
            ? {
                hint: t("analytics.reports.previous", {
                  value: formatFigure(k, report.previous[k]),
                }),
              }
            : {})}
        />
      ))}
    </div>
  );
}

/** The breakdown rows (by the chosen dimension) or, for "Day", the day series. */
export function ReportTable({ report, max }: { report: Report; max?: number }) {
  const headline = HEADLINES[report.topic] ?? [];
  if (report.rows.length) {
    const metrics = Object.keys(report.rows[0]!).filter((k) => k !== "key" && k !== "label");
    const cols: Column<BreakdownRow>[] = [
      {
        id: "key",
        header: t(`analytics.dims.${report.dim}`),
        cell: (r) => keyLabel(report.dim, r),
        userContent: true,
      },
      ...metrics.map((m, i) => ({
        id: m,
        header: metricLabel(m),
        cell: (r: BreakdownRow) => formatFigure(m, r[m] as number),
        numeric: true,
        align: "end" as const,
        priority: headline.includes(m) ? 1 : i + 2,
      })),
    ];
    return (
      <DataTable
        caption={t("analytics.reports.breakdown")}
        columns={cols}
        rows={report.rows.slice(0, max ?? report.rows.length)}
        rowId={(r) => r.key}
        card={{
          title: (r) => keyLabel(report.dim, r),
          details: (r) =>
            headline
              .filter((m) => m in r)
              .slice(0, 3)
              .map((m) => `${metricLabel(m)} ${formatFigure(m, r[m] as number)}`)
              .join(" · "),
        }}
        dense
        data-testid={ATID.table}
      />
    );
  }
  if (!report.series.length) return null;
  const metrics = headline.filter((m) => m in report.series[0]!).slice(0, 4);
  const cols: Column<SeriesPoint>[] = [
    { id: "date", header: t("analytics.dims.total"), cell: (p) => String(p.date) },
    ...metrics.map((m) => ({
      id: m,
      header: metricLabel(m),
      cell: (p: SeriesPoint) => formatFigure(m, p[m] as number),
      numeric: true,
      align: "end" as const,
    })),
  ];
  return (
    <DataTable
      caption={t("analytics.reports.byDay")}
      columns={cols}
      rows={[...report.series].reverse().slice(0, max ?? report.series.length)}
      rowId={(p) => String(p.date)}
      card={{
        title: (p) => String(p.date),
        details: (p) =>
          metrics.map((m) => `${metricLabel(m)} ${formatFigure(m, p[m] as number)}`).join(" · "),
      }}
      dense
      data-testid={ATID.table}
    />
  );
}

export function ReportBody({ report, compact }: { report: Report; compact?: boolean }) {
  if (report.availability === "activation_required") return <TrafficActivation />;
  const empty = Object.values(report.totals).every((v) => !v) && report.rows.length === 0;
  return (
    <div className="flex flex-col gap-3">
      {report.partial ? (
        <Banner
          kind="info"
          title={t("analytics.reports.partialProfit")}
          data-testid={ATID.partial}
        />
      ) : null}
      {empty ? (
        <EmptyState
          size="inline"
          headingLevel={3}
          title={t("analytics.reports.noData")}
          body={t("analytics.reports.noDataBody")}
        />
      ) : (
        <>
          <ReportFigures report={report} {...(compact ? { limit: 3 } : {})} />
          <ReportTable report={report} {...(compact ? { max: 5 } : {})} />
        </>
      )}
      {report.updated_at && !compact ? (
        <p className="text-xs text-fg-tertiary">
          {t("analytics.reports.updated", { when: formatDateTime(report.updated_at) })}
        </p>
      ) : null}
    </div>
  );
}

export function TrafficActivation() {
  return (
    <Banner
      kind="pending-activation"
      title={t("analytics.traffic.activationTitle")}
      data-testid={ATID.trafficActivation}
    >
      {t("analytics.traffic.activationBody")}
    </Banner>
  );
}

export function PlanLocked() {
  return (
    <Banner
      kind="plan-locked"
      title={t("analytics.reports.planLockedTitle")}
      data-testid={ATID.planLocked}
    >
      {t("analytics.reports.planLockedBody")}
    </Banner>
  );
}

const STATE_TONE = {
  queued: "neutral",
  running: "neutral",
  done: "success",
  failed: "danger",
} as const;
const STATE_LABEL = {
  queued: "analytics.export.stateQueued",
  running: "analytics.export.stateQueued",
  done: "analytics.export.stateDone",
  failed: "analytics.export.stateFailed",
} as const;

export function ExportStatus({ exp }: { exp: ReportExport }) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-sm"
      data-testid={ATID.exportStatus}
      data-state={exp.state}
    >
      <Badge tone={STATE_TONE[exp.state]}>{t(STATE_LABEL[exp.state])}</Badge>
      <span className="text-fg-secondary">
        {`${t(`analytics.topics.${exp.topic}`)} · ${exp.from} – ${exp.to}`}
      </span>
      {exp.state === "done" && exp.download ? (
        <a className="font-medium text-action-fg underline" href={exp.download.url} download>
          {t("analytics.export.download")}
        </a>
      ) : null}
      {exp.state === "failed" ? (
        <span className="text-danger-fg">{t("analytics.export.failed")}</span>
      ) : null}
    </div>
  );
}

export function LeaderboardBand({
  view,
  canChange,
  busy,
  onToggle,
}: {
  view: Leaderboard;
  canChange: boolean;
  busy?: boolean;
  onToggle?: (next: boolean) => void;
}) {
  const b = view.band;
  return (
    <div
      className="flex flex-col gap-3"
      data-testid={ATID.leaderboard}
      data-state={b?.state ?? "out"}
    >
      <p className="text-sm text-fg-secondary">{t("analytics.leaderboard.lead")}</p>
      {view.opted_in ? (
        b?.state === "ready" ? (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-0.5 rounded-sm bg-sunken px-3 py-2.5">
                <span className="text-xs text-fg-secondary">
                  {t("analytics.leaderboard.ordersBand")}
                </span>
                <span className="text-title-sm font-semibold">
                  {t(`analytics.leaderboard.bands.${b.orders}`)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 rounded-sm bg-sunken px-3 py-2.5">
                <span className="text-xs text-fg-secondary">
                  {t("analytics.leaderboard.gmvBand")}
                </span>
                <span className="text-title-sm font-semibold">
                  {t(`analytics.leaderboard.bands.${b.gmv}`)}
                </span>
              </div>
            </div>
            <p className="text-xs text-fg-tertiary">
              {t("analytics.leaderboard.cohort", { count: b.cohort_size })}
            </p>
          </div>
        ) : b?.state === "not_enough_data" ? (
          <p className="text-sm">
            {t("analytics.leaderboard.notEnough", { count: view.min_cohort })}
          </p>
        ) : (
          <p className="text-sm">{t("analytics.leaderboard.pending")}</p>
        )
      ) : null}
      {canChange ? (
        <div>
          <Button
            variant="secondary"
            loading={busy ?? false}
            onClick={() => onToggle?.(!view.opted_in)}
            data-testid={ATID.leaderboardToggle}
          >
            {view.opted_in ? t("analytics.leaderboard.optOut") : t("analytics.leaderboard.optIn")}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-fg-tertiary">{t("analytics.leaderboard.ownerOnly")}</p>
      )}
    </div>
  );
}
